// S15 — Files (Media Center) BE client.
//
// BE endpoints (under `/api/v1/admin/files`):
//   POST   /upload          — multipart (single IFormFile `file` + form field `folderId?`)
//   POST   /upload-bulk     — ISSUE-61. multipart with repeated `files` field +
//                             optional `folderId`. 250 MB batch / 25 MB per file.
//                             Returns ActionResponse<BulkUploadResultDto> with
//                             uploadedCount, deduplicatedCount, items[], errors[].
//   GET    /                — paged list with filters
//   GET    /{id}            — detail
//   GET    /{id}/content    — binary stream (or 302 to CDN for S3)
//   PUT    /{id}/rename     — body: { newFileName, rowVersion }
//   POST   /{id}/move       — body: { targetFolderId }
//   DELETE /{id}            — soft delete

import {
  http,
  unwrap,
  unwrapList,
  type ActionResponse,
  type PaginationInfo,
} from './http';
import type { components } from '@/types/api';

export type FileBeDto = components['schemas']['FileDto'];
export type UploadFileResultBeDto = components['schemas']['UploadFileResultDto'];
export type BulkUploadResultBeDto = components['schemas']['BulkUploadResultDto'];
export type BulkUploadErrorBeDto = components['schemas']['BulkUploadErrorDto'];

/** ClamAV scan lifecycle (MOD-02+ / Blok A). Only `Clean`/`Skipped` are servable. */
export type FileScanStatus = components['schemas']['FileScanStatus'];

/** One resized WebP derivative produced by the media pipeline. */
export interface FileVariant {
  url: string | null;
  width: number;
  height: number;
  sizeBytes: number;
}

export interface FileVariants {
  thumb: FileVariant | null;
  medium: FileVariant | null;
  full: FileVariant | null;
}

export interface FileItem {
  id: string;
  fileName: string;
  storedFileName: string | null;
  folderId: string | null;
  mimeType: string;
  sizeBytes: number;
  sha256Hash: string | null;
  storageProvider: 'Local' | 'S3';
  publicUrl: string | null;
  uploadedByUserId: string;
  thumbnailUrl: string | null;
  thumbnailWidth: number | null;
  thumbnailHeight: number | null;
  altText: string | null;
  scanStatus: FileScanStatus;
  variants: FileVariants | null;
  createdAt: string;
  updatedAt: string;
  rowVersion: string | null;
}

export interface UploadFileResult {
  id: string;
  fileName: string;
  sha256Hash: string | null;
  deduplicatedFromExisting: boolean;
  publicUrl: string | null;
}

/** ISSUE-61 — per-file failure row inside a bulk upload response. */
export interface BulkUploadError {
  fileName: string;
  code: string;
  message: string;
}

/** ISSUE-61 — envelope returned by `POST /upload-bulk`. */
export interface BulkUploadResult {
  uploadedCount: number;
  deduplicatedCount: number;
  items: UploadFileResult[];
  errors: BulkUploadError[];
}

// Per-file size + allowed extensions are admin-configurable — see
// features/media/uploadConstraints (parsed from the public upload.* settings).
/** Total batch ceiling — kept ≥ the per-file ceiling so a single max-size file fits in a batch. */
export const FILES_BATCH_LIMIT_BYTES = 500 * 1024 * 1024;

export interface FileListQuery {
  folderId?: string | null;
  mimeType?: string;
  uploadedByUserId?: string;
  fromUtc?: string;
  toUtc?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationInfo | null;
  total: number;
}

function mapVariant(v: components['schemas']['FileVariantDto'] | undefined): FileVariant | null {
  if (!v) return null;
  return {
    url: v.url ?? null,
    width: v.width ?? 0,
    height: v.height ?? 0,
    sizeBytes: v.sizeBytes ?? 0,
  };
}

function mapVariants(v: FileBeDto['variants']): FileVariants | null {
  if (!v) return null;
  return {
    thumb: mapVariant(v.thumb),
    medium: mapVariant(v.medium),
    full: mapVariant(v.full),
  };
}

function mapFile(d: FileBeDto): FileItem {
  return {
    id: d.id ?? '',
    fileName: d.fileName ?? '',
    storedFileName: d.storedFileName ?? null,
    folderId: d.folderId ?? null,
    mimeType: d.mimeType ?? 'application/octet-stream',
    sizeBytes: d.sizeBytes ?? 0,
    sha256Hash: d.sha256Hash ?? null,
    storageProvider: (d.storageProvider ?? 'Local'),
    publicUrl: d.publicUrl ?? null,
    uploadedByUserId: d.uploadedByUserId ?? '',
    thumbnailUrl: d.thumbnailUrl ?? null,
    thumbnailWidth: d.thumbnailWidth ?? null,
    thumbnailHeight: d.thumbnailHeight ?? null,
    altText: d.altText ?? null,
    scanStatus: d.scanStatus ?? 'None',
    variants: mapVariants(d.variants),
    createdAt: d.createdAt ?? '',
    updatedAt: d.updatedAt ?? '',
    rowVersion: d.rowVersion ?? null,
  };
}

/**
 * Preferred servable URL for embedding a file in content. Prefers the
 * pipeline's `medium` (1024w WebP) derivative when present, else falls back
 * to the original `publicUrl`. Callers persist this string into block props.
 */
export function fileEmbedUrl(file: FileItem): string {
  return file.variants?.medium?.url ?? file.publicUrl ?? '';
}

function mapUploadResult(d: UploadFileResultBeDto): UploadFileResult {
  return {
    id: d.id ?? '',
    fileName: d.fileName ?? '',
    sha256Hash: d.sha256Hash ?? null,
    deduplicatedFromExisting: d.deduplicatedFromExisting ?? false,
    publicUrl: d.publicUrl ?? null,
  };
}

function mapBulkError(d: BulkUploadErrorBeDto): BulkUploadError {
  return {
    fileName: d.fileName ?? '',
    code: d.code ?? 'UPLOAD_ERROR',
    message: d.message ?? 'Yükleme başarısız.',
  };
}

function mapBulkResult(d: BulkUploadResultBeDto): BulkUploadResult {
  return {
    uploadedCount: d.uploadedCount ?? 0,
    deduplicatedCount: d.deduplicatedCount ?? 0,
    items: (d.items ?? []).map(mapUploadResult),
    errors: (d.errors ?? []).map(mapBulkError),
  };
}

const BASE = '/api/v1/admin/files';

export const filesApi = {
  async list(query: FileListQuery = {}): Promise<PaginatedResult<FileItem>> {
    const p = new URLSearchParams();
    if (query.folderId) p.set('folderId', query.folderId);
    if (query.mimeType) p.set('mimeType', query.mimeType);
    if (query.uploadedByUserId) p.set('uploadedByUserId', query.uploadedByUserId);
    if (query.fromUtc) p.set('fromUtc', query.fromUtc);
    if (query.toUtc) p.set('toUtc', query.toUtc);
    if (query.search) p.set('search', query.search);
    if (query.page) p.set('page', String(query.page));
    if (query.pageSize) p.set('pageSize', String(query.pageSize));
    const qs = p.toString();
    const res = await http.get<ActionResponse<FileBeDto>>(qs ? `${BASE}?${qs}` : BASE);
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapFile),
      pagination,
      total: pagination?.totalCount ?? items.length,
    };
  },

  async get(id: string): Promise<FileItem> {
    const res = await http.get<ActionResponse<FileBeDto>>(`${BASE}/${id}`);
    return mapFile(unwrap(res));
  },

  /**
   * Upload a single file. `onProgress` receives a percentage 0..100.
   * Pass `Content-Type: undefined` so axios infers the multipart boundary.
   */
  async upload(
    file: File,
    folderId?: string | null,
    onProgress?: (percent: number) => void,
  ): Promise<UploadFileResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (folderId) form.append('folderId', folderId);
    const res = await http.post<ActionResponse<UploadFileResultBeDto>>(
      `${BASE}/upload`,
      form,
      {
        headers: { 'Content-Type': undefined as unknown as string },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      },
    );
    return mapUploadResult(unwrap(res));
  },

  /**
   * ISSUE-61 — Bulk multipart upload. Sends a SINGLE multipart request with
   * the `files` form field repeated once per file (matches BE
   * `IFormFileCollection files`). The BE returns a `BulkUploadResultDto`
   * envelope that captures both successes (`items`) and per-file failures
   * (`errors[]`) so we can surface a partial-success state in the UI.
   *
   * `Content-Type` is intentionally left for the browser to set so the
   * multipart boundary is correct; the existing `'Content-Type': undefined`
   * hack on the single-file endpoint is mirrored here.
   */
  async uploadBulk(
    files: File[],
    folderId?: string | null,
    onProgress?: (percent: number) => void,
  ): Promise<BulkUploadResult> {
    if (files.length === 0) {
      return { uploadedCount: 0, deduplicatedCount: 0, items: [], errors: [] };
    }
    const form = new FormData();
    for (const file of files) {
      // Append once per file using the BE's `files` field name. Browser
      // serializes these as repeated parts in the same multipart body, which
      // .NET binds to `IFormFileCollection`.
      form.append('files', file, file.name);
    }
    if (folderId) form.append('folderId', folderId);
    const res = await http.post<ActionResponse<BulkUploadResultBeDto>>(
      `${BASE}/upload-bulk`,
      form,
      {
        headers: { 'Content-Type': undefined as unknown as string },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      },
    );
    return mapBulkResult(unwrap(res));
  },

  /** Stream a file's binary content as a Blob (for thumbnails / downloads). */
  async download(id: string): Promise<Blob> {
    const res = await http.get(`${BASE}/${id}/content`, { responseType: 'blob' });
    return res.data as Blob;
  },

  /** BIZ-IMG-03 — update editable metadata (alt text) for SEO. */
  async updateMeta(
    id: string,
    altText: string | null,
    rowVersion: string | null,
  ): Promise<void> {
    const res = await http.put<ActionResponse<boolean>>(`${BASE}/${id}/meta`, {
      altText,
      rowVersion,
    });
    unwrap(res);
  },

  async rename(id: string, newFileName: string, rowVersion: string | null): Promise<void> {
    const res = await http.put<ActionResponse<boolean>>(`${BASE}/${id}/rename`, {
      newFileName,
      rowVersion,
    });
    unwrap(res);
  },

  async move(id: string, targetFolderId: string | null): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(`${BASE}/${id}/move`, {
      targetFolderId,
    });
    unwrap(res);
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`);
    unwrap(res);
  },
};
